import { useEffect, useState } from "react";
import router from "../router/index.ts";
import { confirm } from "../util/confirm.tsx";

type ConfirmNavigation = (
	message: string,
	options: {
		okText: string;
		cancelText: string;
		variant?: string;
	},
) => Promise<boolean>;

export const resolveNavigationBlock = async ({
	cancelText,
	confirmNavigation,
	message,
	okText,
	refresh,
	variant,
}: {
	cancelText: string;
	confirmNavigation: ConfirmNavigation;
	message: string;
	okText: string;
	refresh: boolean;
	variant?: string;
}) => {
	// Realtime refreshes update the current view and are not attempts to leave it.
	if (refresh) {
		return false;
	}

	const proceed = await confirmNavigation(message, {
		okText,
		cancelText,
		variant,
	});

	// Router.shouldBlock uses true to preserve the current URL.
	return !proceed;
};

export const useBlocker = ({
	message = "If you navigate away from this page, you will lose any unsaved changes.",
	okText = "Navigate away",
	cancelText = "Stay here",
	initialDirty = false,
	variant,
}: {
	message?: string;
	okText?: string;
	cancelText?: string;
	initialDirty?: boolean;
	variant?: string;
} = {}) => {
	const [dirty, setDirty] = useState(initialDirty);

	useEffect(() => {
		if (dirty) {
			router.shouldBlock = (refresh) =>
				resolveNavigationBlock({
					cancelText,
					confirmNavigation: (confirmation, options) =>
						confirm(confirmation, options),
					message,
					okText,
					refresh,
					variant,
				});

			return () => {
				router.shouldBlock = undefined;
			};
		} else {
			router.shouldBlock = undefined;
		}
	}, [cancelText, dirty, message, okText, variant]);

	return { dirty, setDirty };
};
